import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CHART_HEIGHT } from '@/charts/chart-theme';

/**
 * Loading placeholders, sized to the thing they replace.
 *
 * Every skeleton here reserves the exact final height. The alternative, a
 * spinner in a collapsed box, makes the page reflow when data lands and reads
 * as slower even when it is not.
 */

export function TileSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-4 w-28" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  );
}

export function ChartSkeleton({ height = CHART_HEIGHT }: { height?: number }) {
  return (
    <div style={{ height }} className="flex items-end gap-1.5 px-1 pb-6">
      {Array.from({ length: 24 }, (_, index) => (
        <Skeleton
          key={index}
          className="flex-1"
          // A flat block reads as a broken chart. Varying the heights on a
          // fixed curve keeps it obviously a placeholder without animating
          // anything that could be mistaken for data.
          style={{ height: `${30 + ((index * 37) % 60)}%` }}
        />
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className="h-5"
              style={{ width: columnIndex === 0 ? '38%' : `${62 / (columns - 1)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
}
